// 거래소 창(docs/PARTY-TRADE-PRD.md 4.1, 레인 D 2단계 UI). 서버 호출은 MarketClient(src/market/marketClient.ts)가 하고, 이 파일은 화면과 입력 검사만 한다.
//
// ── 게임 레인이 main.ts에서 부르는 방법 ─────────────────────────────────────────
//   import { MarketClient } from './market/marketClient';
//   import { mountMarketUi } from './ui/marketPanel';
//   const ledger: MarketLedger = { bagItems, gold, out, goldOut };           // src/game/marketApply.ts
//   const marketUi = mountMarketUi({ market: new MarketClient(ledger), ledger, inVillage: () => view.flow.scene === Scene.Village, openMail: () => mailUi.open() });
// ── 시그니처 ──────────────────────────────────────────────────────────────────
//   mountMarketUi(host: MarketUiHost): MarketUi
//   MarketUiHost { market: MarketClient; ledger: MarketLedger; inVillage: () => boolean; openMail?: () => void(기본: PARTY_MENU_EVENT 'mail'); parent?: HTMLElement; now?: () => number }
//   MarketUi { open(); close(); toggle(); isOpen(); refresh(): Promise<void>; dispose() }
// 「거래소」 단추(menuBar, marketEnabledHere()일 때만 「더보기」 줄)는 PARTY_MENU_EVENT('market')로 알리고 이 창이 토글한다.
// 창을 열 때마다 me() → reconcile(me) 한 번(서버가 맡은 장비 사본·ack 안 된 골드 맡기기를 가방과 맞춘다).
// 탭 4개: 둘러보기(필터·정렬·20개/쪽·상세·입찰·즉시 구매 2단계) · 판매(맡기기 → 등록 → 내 등록) · 금고(골드·보관함) · 우편함(기존 우편 창을 연다).
// 모든 오류는 MARKET_ERROR_TEXT 한 줄. canSell=false면 판매·입찰 대신 이유(link-required / not-eligible).
import { MARKET, minNextBid, priceBounds, saleFee, sellerGets, isTradable, validPrice } from '../shared/market';
import type { GearItem } from '../shared/gear';
import { GRADES } from '../shared/content/items';
import { MARKET_DAILY_GOLD } from '../shared/content/economy';
import { MARKET_ERROR_TEXT, type ListingsPage, type MarketClient, type MarketLedger, type MarketMe, type MarketResult, type PriceView } from '../market/marketClient';
import type { ListingView } from '../server/market';
import { assetUrl } from '../pwa/assetMap';
import { el } from './dom';
import { gearCard, SLOT_KO, type GearCard } from './gearModel';
import { PARTY_MENU_EVENT } from './menuBar';
import { PARTY_WINDOW_EVENT, ensurePartyStyle } from './partyStyle';

export interface MarketUiHost {
  market: MarketClient;
  ledger: MarketLedger;
  inVillage: () => boolean;
  openMail?: () => void;
  parent?: HTMLElement;
  now?: () => number;
}
export interface MarketUi { open(): void; close(): void; toggle(): void; isOpen(): boolean; refresh(): Promise<void>; dispose(): void }

// ── 순수 표시·검사 규칙(시험 대상) ──────────────────────────────────

/** 지금 가격: 입찰이 있으면 그 금액, 없으면 시작가 */
export function listingPriceNow(l: Pick<ListingView, 'bid' | 'start'>): number { return l.bid > 0 ? l.bid : l.start; }
const MIN = 60_000, HOUR = 3_600_000, DAY = 86_400_000;
export function remainText(endsAt: number, now: number): string {
  const left = endsAt - now;
  if (left <= 0) return '마감';
  if (left >= DAY) return Math.floor(left / DAY) + '일 ' + Math.floor((left % DAY) / HOUR) + '시간';
  if (left >= HOUR) return Math.floor(left / HOUR) + '시간 ' + Math.floor((left % HOUR) / MIN) + '분';
  return Math.max(1, Math.ceil(left / MIN)) + '분';
}
export const gold = (n: number): string => n.toLocaleString('ko-KR');
/** 입력 칸 문자열 → 자연수(쉼표·공백 허용). 아니면 0 */
export function parseGold(s: string): number {
  const t = s.replace(/[,\s]/g, '');
  if (!/^\d{1,12}$/.test(t)) return 0;
  const n = Number(t);
  return Number.isSafeInteger(n) ? n : 0;
}
/** 판매 시 받는 골드 미리보기(수수료 5% 소각) */
export function sellPreview(price: number): { fee: number; gets: number } { return { fee: saleFee(price), gets: sellerGets(price) }; }

export type BidCheck = { ok: true } | { ok: false; code: string };
/** 입찰 검사: 내 물건·금액·금고 잔액. code는 MARKET_ERROR_TEXT 열쇠 */
export function bidCheck(l: ListingView, amount: number, wallet: number): BidCheck {
  if (l.mine) return { ok: false, code: 'own-listing' };
  if (!validPrice(amount) || amount < l.minBid) return { ok: false, code: 'bid-low' };
  if (l.buyout > 0 && amount >= l.buyout) return { ok: false, code: 'use-buyout' };
  // 내가 앞선 입찰자면 내 앞 입찰액은 서버가 먼저 돌려준다(금고 + 내 입찰 ≥ 새 입찰)
  if (wallet + (l.leading ? l.bid : 0) < amount) return { ok: false, code: 'insufficient' };
  return { ok: true };
}
/** 즉시 구매는 즉구가가 있고 지금 입찰가보다 높을 때만 */
export function canBuyout(l: ListingView): boolean { return !l.mine && l.buyout > 0 && l.buyout > l.bid; }

export type SellCheck = { ok: true } | { ok: false; code: string };
/** 등록 검사: 시작가·즉구가(0 = 없음)가 가격 범위 안이고 즉구가 > 시작가 */
export function sellCheck(item: GearItem, start: number, buyout: number): SellCheck {
  if (!validPrice(start)) return { ok: false, code: 'price-range' };
  if (buyout !== 0 && (!validPrice(buyout) || buyout <= start)) return { ok: false, code: 'price-range' };
  const b = priceBounds(item);
  if (start < b.min || start > b.max || buyout > b.max) return { ok: false, code: 'price-range' };
  return { ok: true };
}
/** 판매 가능한 가방 장비: 거래 가능 + 서버가 이미 맡은 uid 제외(장착 제외는 ledger.bagItems가 이미 한다) */
/** 출석 줄 문구: 연속 n일째, 다음 지급액(7일째 보너스 안내) */
export function dailyLabel(d: { claimed: boolean; streak: number; next: number }): string {
  if (d.claimed) return '출석 ' + d.streak + '일째 · 오늘 받았어요';
  return '출석 골드 ' + d.next.toLocaleString('ko-KR') + ' (연속 ' + (d.streak + 1) + '일째' + (d.next > MARKET_DAILY_GOLD.base ? ' 보너스' : '') + ')';
}

export function sellableBag(bag: readonly GearItem[], held: readonly string[]): GearItem[] {
  const h = new Set(held);
  return bag.filter((it) => isTradable(it) && !h.has(it.uid));
}
/** 오류 한 줄(MARKET_ERROR_TEXT, 가격 범위는 허용 범위를 덧붙인다) */
export function marketErrorText(code: string, reason?: string): string {
  if (code === 'price-range' && reason && /^\d+-\d+$/.test(reason)) { const [a, b] = reason.split('-'); return MARKET_ERROR_TEXT['price-range'] + ' (' + gold(Number(a)) + ' ~ ' + gold(Number(b)) + ' 골드)'; }
  if (code === 'not-eligible' && reason === 'account-age') return MARKET_ERROR_TEXT['not-eligible'];
  return MARKET_ERROR_TEXT[code] ?? (code === 'bad-request' ? '입력을 확인해 주세요.' : '문제가 생겼어요. 잠시 뒤 다시 해 주세요. (' + code + ')');
}
/** 판매·입찰을 못 하는 이유 줄들(canSell=false) */
export function sellBlockLines(): string[] { return [MARKET_ERROR_TEXT['link-required'], MARKET_ERROR_TEXT['not-eligible']]; }
/** 시세 막대에서 중앙값의 위치(0~100%) */
export function priceMarkPct(p: Pick<PriceView, 'min' | 'max' | 'median'>): number {
  if (p.min === null || p.max === null || p.median === null) return 50;
  if (p.max <= p.min) return 50;
  return Math.round(((p.median - p.min) / (p.max - p.min)) * 100);
}
export const GRADE_FILTERS: readonly { id: string; name: string }[] = [{ id: '', name: '모든 등급' }, ...GRADES.filter((g) => g.id !== 'common').map((g) => ({ id: g.id as string, name: g.name }))];
export const SLOT_FILTERS: readonly { id: string; name: string }[] = [{ id: '', name: '모든 부위' }, ...Object.entries(SLOT_KO).map(([id, name]) => ({ id, name }))];
export const SORTS: readonly { id: 'ends' | 'price' | 'buyout'; name: string }[] = [{ id: 'ends', name: '마감 임박순' }, { id: 'price', name: '현재가 낮은순' }, { id: 'buyout', name: '즉구가 낮은순' }];

type Tab = 'browse' | 'sell' | 'vault' | 'mail';
const TABS: readonly { id: Tab; name: string }[] = [{ id: 'browse', name: '둘러보기' }, { id: 'sell', name: '판매' }, { id: 'vault', name: '금고' }, { id: 'mail', name: '우편함' }];

export function mountMarketUi(host: MarketUiHost): MarketUi {
  ensurePartyStyle();
  const parent = host.parent ?? document.body;
  const now = host.now ?? Date.now;
  const mk = host.market;
  const village = (): boolean => { try { return host.inVillage(); } catch { return false; } };

  const win = el('div', '', parent);
  win.className = 'dh-pw wide';
  win.dataset.testid = 'market-window';
  win.setAttribute('role', 'dialog');
  win.setAttribute('aria-label', '거래소');
  const hd = el('div', '', win); hd.className = 'hd';
  const title = el('h2', '', hd, '거래소');
  const xb = el('button', '', hd, '✕'); xb.className = 'x'; xb.type = 'button'; xb.setAttribute('aria-label', '거래소 닫기'); xb.dataset.testid = 'market-close';
  const tabsEl = el('div', '', win); tabsEl.className = 'tabs'; tabsEl.setAttribute('role', 'tablist');
  const bd = el('div', '', win); bd.className = 'bd';
  const ft = el('div', '', win); ft.className = 'ft';

  // ── 상태 ──
  let opened = false;
  let tab: Tab = 'browse';
  let me: MarketMe | null = null;
  let meErr = '';
  let pageData: ListingsPage | null = null;
  let pageErr = '';
  let loading = false;
  const filter: { slot: string; grade: string; sort: 'ends' | 'price' | 'buyout'; page: number } = { slot: '', grade: '', sort: 'ends', page: 0 };
  let detail: ListingView | null = null;
  let price: PriceView | null = null;
  let priceFor = '';
  let confirmBuy = false;
  let bidText = '';
  let sellUid = '';
  const form = { start: '', buyout: '', hours: 24 };
  let goldIn = '';
  let goldOut = '';
  let msg: { text: string; tone: '' | 'ok' | 'err' } = { text: '', tone: '' };
  let busy = false;

  const say = (text: string, tone: '' | 'ok' | 'err' = ''): void => { msg = { text, tone }; };
  const fail = (r: { code: string; reason?: string }): void => say(marketErrorText(r.code, r.reason), 'err');
  const canSell = (): boolean => !!me && me.canSell;

  // ── 불러오기 ──
  async function loadMe(): Promise<void> {
    const r = await mk.me();
    if (!r.ok) { meErr = marketErrorText(r.code, r.reason); me = null; return; }
    meErr = '';
    let data = r.data;
    try {
      if (await mk.reconcile(data) > 0) { const r2 = await mk.me(); if (r2.ok) data = r2.data; }
    } catch { /* 맞추기 실패는 다음에 다시 */ }
    me = data;
  }
  async function loadListings(): Promise<void> {
    const r = await mk.listings({ slot: filter.slot || undefined, grade: filter.grade || undefined, sort: filter.sort, page: filter.page || undefined });
    if (!r.ok) { pageErr = marketErrorText(r.code, r.reason); pageData = null; return; }
    pageErr = ''; pageData = r.data;
    if (detail) { const f = r.data.listings.find((x) => x.id === detail!.id); if (f) detail = f; }
  }
  async function reload(all = true): Promise<void> {
    loading = true; render();
    await (all ? Promise.all([loadMe(), loadListings()]) : loadMe());
    loading = false; render();
  }
  async function loadPrice(l: ListingView): Promise<void> {
    const k = l.item.def + '|' + l.item.grade;
    if (priceFor === k) return;
    priceFor = k; price = null;
    const r = await mk.price(l.item.def, l.item.grade);
    if (priceFor === k && r.ok) { price = r.data; render(); }
  }
  async function act<T>(run: () => Promise<MarketResult<T>>, okText: string, after?: (d: T) => void): Promise<void> {
    if (busy) return;
    busy = true; say('처리 중…'); render();
    const r = await run();
    if (r.ok) { say(okText, 'ok'); after?.(r.data); } else fail(r);
    busy = false;
    await reload();
  }

  // ── 그리기 도우미 ──
  const btn = (p: HTMLElement, label: string, fn: () => void, cls = '', testid = ''): HTMLButtonElement => {
    const b = el('button', '', p, label); b.type = 'button'; b.className = 'btn' + (cls ? ' ' + cls : '');
    if (testid) b.dataset.testid = testid;
    b.addEventListener('click', fn);
    return b;
  };
  const sec = (label: string, inFoot = false): HTMLDivElement => { const s = el('div', '', inFoot ? ft : bd); s.className = 'sec'; if (label) el('div', '', s, label).className = 'lb'; return s; };
  const note = (p: HTMLElement, text: string, cls = ''): HTMLElement => { const n = el('div', '', p, text); n.className = 'note' + (cls ? ' ' + cls : ''); return n; };
  const kv = (p: HTMLElement, k: string, v: string): void => { const r = el('div', '', p); r.className = 'kv'; el('span', '', r, k); el('b', '', r, v); };
  const numInput = (p: HTMLElement, value: string, ph: string, label: string, set: (v: string) => void, testid: string): HTMLInputElement => {
    const i = el('input', '', p); i.className = 'num'; i.type = 'text'; i.inputMode = 'numeric'; i.placeholder = ph; i.value = value; i.autocomplete = 'off';
    i.setAttribute('aria-label', label); i.dataset.testid = testid;
    i.addEventListener('input', () => { set(i.value); });
    i.addEventListener('keydown', (e) => e.stopPropagation());
    i.addEventListener('keyup', (e) => e.stopPropagation());
    return i;
  };
  const iconBox = (p: HTMLElement, c: GearCard | null, coin = false): void => {
    const ic = el('div', '', p); ic.className = 'ic';
    const im = el('img', '', ic); im.alt = ''; im.draggable = false;
    const list = coin || !c ? ['ui/items/cur_gold.webp'] : [c.icon, ...c.iconFallbacks];
    let i = 0;
    im.onerror = () => { i++; if (i < list.length) im.src = assetUrl(list[i]); else im.style.visibility = 'hidden'; };
    im.src = assetUrl(list[0]);
    if (c && !coin) ic.style.borderColor = c.color;
  };
  const nameOf = (c: GearCard): string => c.name + (c.enh > 0 ? ' +' + c.enh : '');
  const subOf = (c: GearCard): string => c.gradeName + ' ' + c.slotName + ' · 아이템 Lv.' + c.ilvl;

  /** 목록 한 줄(눌러서 상세) */
  function listingRow(p: HTMLElement, l: ListingView): void {
    const c = gearCard(l.item, false);
    const b = el('button', '', p); b.type = 'button'; b.className = 'lst' + (l.mine ? ' mine' : ''); b.dataset.testid = 'market-listing';
    iconBox(b, c);
    const tx = el('div', '', b); tx.className = 'tx';
    const nm = el('div', '', tx, nameOf(c)); nm.className = 'nm'; nm.style.color = c.color;
    if (l.mine) el('span', '', nm, '내 물건').className = 'badge me';
    else if (l.leading) el('span', '', nm, '최고 입찰').className = 'badge';
    el('div', '', tx, subOf(c)).className = 'sub';
    el('div', '', tx, '입찰 ' + l.bids + '명 · ' + remainText(l.endsAt, now())).className = 'sub';
    const pr = el('div', '', b); pr.className = 'pr';
    el('b', '', pr, gold(listingPriceNow(l)));
    el('span', '', pr, l.buyout > 0 ? '즉구 ' + gold(l.buyout) : '즉구 없음');
    b.addEventListener('click', () => { detail = l; confirmBuy = false; bidText = String(l.minBid); say(''); void loadPrice(l); render(); });
  }
  function bagRow(p: HTMLElement, it: GearItem, right: (row: HTMLElement) => void): void {
    const c = gearCard(it, false);
    const r = el('div', '', p); r.className = 'lst'; r.style.cursor = 'default';
    iconBox(r, c);
    const tx = el('div', '', r); tx.className = 'tx';
    const nm = el('div', '', tx, nameOf(c)); nm.className = 'nm'; nm.style.color = c.color;
    el('div', '', tx, subOf(c)).className = 'sub';
    right(r);
  }

  // ── 탭 화면 ──
  function renderBrowse(): void {
    const f = sec('');
    const row = el('div', '', f); row.className = 'filters';
    const sel = (opts: readonly { id: string; name: string }[], cur: string, label: string, set: (v: string) => void, tid: string): void => {
      const s = el('select', '', row); s.setAttribute('aria-label', label); s.dataset.testid = tid;
      for (const o of opts) { const op = el('option', '', s, o.name); op.value = o.id; op.selected = o.id === cur; }
      s.addEventListener('change', () => { set(s.value); filter.page = 0; void reload(true); });
    };
    sel(SLOT_FILTERS, filter.slot, '부위', (v) => { filter.slot = v; }, 'market-f-slot');
    sel(GRADE_FILTERS, filter.grade, '등급', (v) => { filter.grade = v; }, 'market-f-grade');
    const row2 = el('div', '', f); row2.className = 'filters';
    const s2 = el('select', '', row2); s2.setAttribute('aria-label', '정렬'); s2.dataset.testid = 'market-f-sort';
    for (const o of SORTS) { const op = el('option', '', s2, o.name); op.value = o.id; op.selected = o.id === filter.sort; }
    s2.addEventListener('change', () => { filter.sort = s2.value as typeof filter.sort; filter.page = 0; void reload(true); });
    if (pageErr) note(bd, pageErr, 'err');
    else if (!pageData) note(bd, '불러오는 중…');
    else if (pageData.listings.length === 0) { const n = note(bd, '등록된 물건이 없어요.'); n.dataset.testid = 'market-empty'; }
    else for (const l of pageData.listings) listingRow(bd, l);
    if (pageData && (pageData.page > 0 || pageData.more)) {
      const pg = el('div', '', ft); pg.className = 'pager';
      const prev = btn(pg, '이전', () => { filter.page = Math.max(0, filter.page - 1); void reload(true); }, 'sm'); prev.disabled = filter.page === 0;
      el('span', '', pg, (filter.page + 1) + ' 쪽');
      const next = btn(pg, '다음', () => { filter.page++; void reload(true); }, 'sm'); next.disabled = !pageData.more;
    }
  }

  function renderDetail(l: ListingView): void {
    const c = gearCard(l.item, false);
    const top = sec('');
    const r0 = el('div', '', top); r0.className = 'lst'; r0.style.cursor = 'default'; r0.style.border = '0'; r0.style.background = 'transparent'; r0.style.padding = '0';
    iconBox(r0, c);
    const tx = el('div', '', r0); tx.className = 'tx';
    const nm = el('div', '', tx, nameOf(c)); nm.className = 'nm'; nm.style.color = c.color; nm.style.whiteSpace = 'normal';
    el('div', '', tx, subOf(c)).className = 'sub';
    for (const s of c.stats) el('div', '', top, s).className = 'stat';
    for (const a of c.affixes) el('div', '', top, a).className = 'stat af';
    if (c.unique) el('div', '', top, c.unique).className = 'stat un';
    // 이름 있는 유니크 효과(거래 가능 장비의 핵심 — PR #246 Codex P2)
    for (const line of c.namedLines ?? []) el('div', '', top, line).className = 'stat un';
    if (c.sockets) el('div', '', top, c.sockets).className = 'stat';

    const st = sec('입찰·구매');
    kv(st, '현재가', gold(listingPriceNow(l)) + ' 골드');
    kv(st, '즉시 구매가', l.buyout > 0 ? gold(l.buyout) + ' 골드' : '없음');
    kv(st, '입찰 / 남은 시간', l.bids + '명 / ' + remainText(l.endsAt, now()));
    kv(st, '내 금고', gold(me?.wallet ?? 0) + ' 골드');
    if (l.mine) note(st, MARKET_ERROR_TEXT['own-listing']);
    else if (!canSell()) { for (const t of sellBlockLines()) note(st, t, 'err'); }
    else {
      el('div', '', st, '내 입찰가 (최소 ' + gold(l.minBid) + ')').className = 'lb';
      numInput(st, bidText, String(l.minBid), '입찰가', (v) => { bidText = v; }, 'market-bid-input');
      const chk = bidCheck(l, parseGold(bidText), me?.wallet ?? 0);
      if (!chk.ok && chk.code === 'insufficient') {
        note(st, MARKET_ERROR_TEXT.insufficient, 'err');
        btn(st, '골드 맡기기', () => { tab = 'vault'; detail = null; render(); }, 'sm', 'market-to-vault');
      }
    }

    const pr = sec('7일 시세');
    if (!price) note(pr, '불러오는 중…');
    else if (price.count === 0) note(pr, '최근 7일 거래가 없어요.');
    else {
      kv(pr, '중앙값 (' + price.count + '건)', gold(price.median ?? 0) + ' 골드');
      kv(pr, '최저 ~ 최고', gold(price.min ?? 0) + ' ~ ' + gold(price.max ?? 0));
      const rng = el('div', '', pr); rng.className = 'rng'; el('i', '', rng).style.left = 'calc(' + priceMarkPct(price) + '% - 2px)';
    }

    // 하단 고정: 입찰 / 즉시 구매(확인 2단계) / 뒤로
    const act2 = sec('', true);
    const row = el('div', '', act2); row.className = 'row';
    if (confirmBuy && canBuyout(l)) {
      note(act2, '정말 ' + gold(l.buyout) + ' 골드에 즉시 구매할까요?', '');
      const r2 = el('div', '', act2); r2.className = 'row';
      btn(r2, '구매 확인', () => { confirmBuy = false; void act(() => mk.buyout(l.id), '구매했어요. 장비는 우편함으로 와요.', () => { detail = null; }); }, 'pri grow', 'market-buyout-yes').disabled = busy;
      btn(r2, '취소', () => { confirmBuy = false; render(); }, 'grow');
    } else {
      if (!l.mine && canSell()) {
        const bb = btn(row, '입찰', () => {
          const amt = parseGold(bidText), chk = bidCheck(l, amt, me?.wallet ?? 0);
          if (!chk.ok) { say(marketErrorText(chk.code), 'err'); render(); return; }
          void act(() => mk.bid(l.id, amt), '입찰했어요.');
        }, 'pri grow', 'market-bid');
        bb.disabled = busy;
      }
      if (canBuyout(l)) btn(row, '즉시 구매', () => { confirmBuy = true; say(''); render(); }, (canSell() ? '' : 'pri ') + 'grow', 'market-buyout').disabled = busy;
      btn(row, '뒤로', () => { detail = null; say(''); render(); }, '', 'market-back');
    }
  }

  function renderSell(): void {
    if (!me) { note(bd, meErr || '불러오는 중…', meErr ? 'err' : ''); return; }
    if (!me.canSell) { const s = sec('판매할 수 없어요'); for (const t of sellBlockLines()) note(s, t, 'err'); }
    const m = me;
    // 등록 폼(보관함 장비를 골랐을 때)
    const chosen = m.vault.find((v) => v.uid === sellUid);
    if (chosen && m.canSell) {
      const c = gearCard(chosen, false), b = priceBounds(chosen);
      const s = sec('등록하기');
      bagRow(s, chosen, () => { /* 이름만 */ });
      el('div', '', s, '시작가 (골드)').className = 'lb';
      let pv: HTMLElement;
      const upd = (): void => {
        const st = parseGold(form.start), bo = parseGold(form.buyout);
        const lines: string[] = [];
        if (st > 0) { const p = sellPreview(st); lines.push('시작가로 팔리면 받는 골드: ' + gold(p.gets) + ' (수수료 5% ' + gold(p.fee) + ' 소각)'); }
        if (bo > 0) { const p = sellPreview(bo); lines.push('즉구가로 팔리면 받는 골드: ' + gold(p.gets) + ' (수수료 ' + gold(p.fee) + ')'); }
        pv.textContent = lines.join('\n') || '가격을 넣으면 받는 골드를 미리 보여 드려요.';
        pv.style.whiteSpace = 'pre-line';
      };
      numInput(s, form.start, gold(b.min), '시작가', (v) => { form.start = v; upd(); }, 'market-sell-start');
      el('div', '', s, '즉시 구매가 (선택, 시작가보다 높게)').className = 'lb';
      numInput(s, form.buyout, '없음', '즉시 구매가', (v) => { form.buyout = v; upd(); }, 'market-sell-buyout');
      note(s, '허용 가격 범위: ' + gold(b.min) + ' ~ ' + gold(b.max) + ' 골드');
      pv = el('div', '', s); pv.className = 'note ok'; pv.dataset.testid = 'market-sell-preview';
      el('div', '', s, '기간').className = 'lb';
      const seg = el('div', '', s); seg.className = 'seg';
      for (const h of MARKET.hours) {
        const hb = btn(seg, h + '시간', () => { form.hours = h; render(); }, form.hours === h ? 'on' : '', 'market-hours-' + h);
        hb.setAttribute('aria-pressed', form.hours === h ? 'true' : 'false');
      }
      upd();
      const row = el('div', '', s); row.className = 'row';
      btn(row, '등록', () => {
        const st = parseGold(form.start), bo = parseGold(form.buyout);
        const chk = sellCheck(chosen, st, bo);
        if (!chk.ok) { say(marketErrorText(chk.code, b.min + '-' + b.max), 'err'); render(); return; }
        void act(() => mk.list(chosen.uid, st, bo, form.hours), '등록했어요.', () => { sellUid = ''; form.start = ''; form.buyout = ''; });
      }, 'pri grow', 'market-list').disabled = busy;
      btn(row, '취소', () => { sellUid = ''; render(); }, 'grow');
      void c;
    }
    // 가방의 거래 가능 장비 → 맡기기
    const bag = sellableBag(host.ledger.bagItems(), m.held);
    const s1 = sec('가방의 거래 가능 장비');
    if (bag.length === 0) note(s1, '거래할 수 있는 장비가 없어요. (이름 있는 유니크만 거래할 수 있어요. 장착 중 제외)');
    for (const it of bag) bagRow(s1, it, (row) => { const b = btn(row, '맡기기', () => { void act(() => mk.deposit(it), '보관함에 맡겼어요. 아래에서 등록하세요.'); }, 'sm pri', 'market-deposit'); b.disabled = busy || !m.canSell; });
    // 보관함 → 등록
    const s2 = sec('보관함 (등록 전)');
    if (m.vault.length === 0) note(s2, '보관함이 비었어요.');
    for (const it of m.vault) bagRow(s2, it, (row) => { const b = btn(row, '등록', () => { sellUid = it.uid; form.start = String(priceBounds(it).min); form.buyout = ''; say(''); render(); }, 'sm pri', 'market-to-list'); b.disabled = !m.canSell; });
    // 내 등록
    const s3 = sec('내 등록 (최대 ' + MARKET.maxActive + '개)');
    const mine = m.listings;
    if (mine.length === 0) note(s3, '등록한 물건이 없어요.');
    for (const l of mine) {
      bagRow(s3, l.item, (row) => {
        const pr = el('div', '', row); pr.className = 'pr';
        el('b', '', pr, gold(l.status === 'sold' && l.price ? l.price : listingPriceNow(l)));
        if (l.status === 'active') {
          el('span', '', pr, '입찰 ' + l.bids + '명 · ' + remainText(l.endsAt, now()));
          const b = btn(pr, '취소', () => { void act(() => mk.cancel(l.id), '취소했어요. 장비는 보관함으로 돌아와요.'); }, 'sm warn', 'market-cancel');
          b.disabled = busy || l.bid > 0;
          if (l.bid > 0) el('span', '', pr, '입찰이 있어 취소 불가');
        } else el('span', '', pr, l.status === 'sold' ? '판매됨' : l.status === 'expired' ? '유찰' : '취소됨');
      });
    }
  }

  function renderVault(): void {
    if (!me) { note(bd, meErr || '불러오는 중…', meErr ? 'err' : ''); return; }
    const m = me;
    const s1 = sec('거래 금고');
    kv(s1, '금고 잔액', gold(m.wallet) + ' 골드');
    kv(s1, '내 가방 골드', gold(host.ledger.gold()) + ' 골드');
    // 출석 금고 지급(서버 출처 골드 — 하루 1번, 7일 연속째 보너스)
    const dl = m.daily;
    if (dl) {
      el('div', '', s1, dailyLabel(dl)).className = 'lb';
      const b = btn(s1, dl.claimed ? '오늘 받음' : '출석 골드 받기', () => {
        void act(() => mk.daily(), '출석 골드를 금고에 넣었어요.');
      }, dl.claimed ? '' : 'pri', 'market-daily');
      b.disabled = busy || dl.claimed || !m.canSell;
    }
    // 클라 골드 맡기기는 서버가 열어 둘 때만(D안 — 기본 닫힘: 금고는 판매 대금·서버 지급 골드로 채운다)
    if (m.goldDeposit === true) {
      el('div', '', s1, '골드 맡기기 (하루 ' + gold(MARKET.goldDepositPerDay) + '까지)').className = 'lb';
      numInput(s1, goldIn, '금액', '맡길 골드', (v) => { goldIn = v; }, 'market-gold-in');
      btn(s1, '맡기기', () => {
        const a = parseGold(goldIn);
        if (!a) { say(marketErrorText('bad-request'), 'err'); render(); return; }
        void act(() => mk.depositGold(a), '금고에 맡겼어요.', () => { goldIn = ''; });
      }, 'pri', 'market-gold-deposit').disabled = busy;
    } else note(s1, '금고는 판매 대금으로 채워져요. 가방 골드 맡기기는 아직 열리지 않았어요.');
    el('div', '', s1, '출금 (골드 우편으로 와요)').className = 'lb';
    numInput(s1, goldOut, '금액', '출금할 골드', (v) => { goldOut = v; }, 'market-gold-out');
    btn(s1, '출금', () => {
      const a = parseGold(goldOut);
      if (!a) { say(marketErrorText('bad-request'), 'err'); render(); return; }
      void act(() => mk.withdrawGold(a), '출금했어요. 우편함에서 받으세요.', () => { goldOut = ''; });
    }, '', 'market-gold-withdraw').disabled = busy;
    const s2 = sec('보관함');
    if (m.vault.length === 0) note(s2, '보관함이 비었어요.');
    for (const it of m.vault) bagRow(s2, it, (row) => { btn(row, '꺼내기', () => { void act(() => mk.withdraw(it.uid), '우편으로 보냈어요. 우편함에서 받으세요.'); }, 'sm', 'market-withdraw').disabled = busy; });
  }

  function render(): void {
    const show = opened && village();
    win.classList.toggle('on', show);
    if (!show) return;
    const keepScroll = bd.scrollTop;
    tabsEl.textContent = ''; bd.textContent = ''; ft.textContent = '';
    for (const t of TABS) {
      const b = el('button', '', tabsEl, t.name); b.type = 'button'; b.className = 'tab' + (t.id === tab ? ' on' : ''); b.setAttribute('role', 'tab'); b.setAttribute('aria-selected', t.id === tab ? 'true' : 'false'); b.dataset.testid = 'market-tab-' + t.id;
      b.addEventListener('click', () => {
        if (t.id === 'mail') { if (host.openMail) host.openMail(); else window.dispatchEvent(new CustomEvent<string>(PARTY_MENU_EVENT, { detail: 'mail' })); return; }
        tab = t.id; detail = null; say(''); render();
        // 창을 연 채로 시간이 지나면 금고·보관함이 바뀌어 있을 수 있다(내 물건이 팔림 등) — 판매·금고 탭을 열 때마다 내 정보를 다시 읽는다
        if (t.id === 'sell' || t.id === 'vault') void reload(false);
      });
    }
    title.textContent = detail && tab === 'browse' ? '물건 정보' : '거래소';
    if (meErr && !me) note(bd, meErr, 'err');
    if (tab === 'browse') { if (detail) renderDetail(detail); else renderBrowse(); }
    else if (tab === 'sell') renderSell();
    else if (tab === 'vault') renderVault();
    if (msg.text) { const n = note(bd, msg.text, msg.tone); n.dataset.testid = 'market-note'; }
    if (loading && !pageData && tab === 'browse') { /* 목록 자리에 불러오는 중 문구가 이미 있다 */ }
    bd.scrollTop = keepScroll;
  }

  function setOpen(on: boolean): void {
    if (on && !village()) return;
    if (on === opened) return;
    opened = on;
    if (on) { window.dispatchEvent(new CustomEvent<string>(PARTY_WINDOW_EVENT, { detail: 'market' })); tab = 'browse'; detail = null; say(''); void reload(true); }
    render();
  }
  const onMenu = (e: Event): void => { if ((e as CustomEvent<string>).detail === 'market') setOpen(!opened); };
  const onWin = (e: Event): void => { const d = (e as CustomEvent<string>).detail; if (d !== 'market' && opened) { opened = false; render(); } };
  window.addEventListener(PARTY_MENU_EVENT, onMenu);
  window.addEventListener(PARTY_WINDOW_EVENT, onWin);
  xb.addEventListener('click', () => setOpen(false));
  win.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setOpen(false); e.stopPropagation(); } });
  const timer = setInterval(() => { if (opened && !village()) { opened = false; render(); } }, 1000);

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!opened),
    isOpen: () => opened && win.classList.contains('on'),
    refresh: () => reload(true),
    dispose: () => { clearInterval(timer); window.removeEventListener(PARTY_MENU_EVENT, onMenu); window.removeEventListener(PARTY_WINDOW_EVENT, onWin); win.remove(); },
  };
}
